import * as React from 'react'
import EditIcon from '@mui/icons-material/Edit'
import PreviewIcon from '@mui/icons-material/Preview'
import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import {
  GridRowsProp,
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowId,
  GridToolbar,
} from '@mui/x-data-grid'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { LicenseModel } from 'types'
import axios from 'axios'
import { base_url } from 'env'
import { toast } from 'react-hot-toast'
import { Box } from '@mui/material'
import SearchBar from './SearchBar'
import ExportButton from './ExportButton'

const searchFields = [
  { value: 'licenseKey', label: 'Clé de licence' },
  { value: 'invitedUserLogin', label: 'Possesseur (Login)' },
  { value: 'creator', label: 'Créateur (Login)' },
  { value: 'isMedical', label: 'Médicale (true/false)' },
]

export default function LicensesDataGrid({
  licenses,
}: {
  licenses: LicenseModel[]
}) {
  const [rows, setRows] = React.useState<GridRowsProp>([])
  const [filteredLicenses, setFilteredLicenses] = React.useState(licenses)
  const navigate = useNavigate()

  React.useEffect(() => {
    setFilteredLicenses(licenses)
  }, [licenses])

  React.useEffect(() => {
    const newRows = filteredLicenses.map((value) => ({
      ...value,
      id: value.licenseKey,
      invitedUserLogin: value.invitedUserLogin || 'N/A',
      usedDate: value.usedDate ? dayjs.unix(value.usedDate).toDate() : null,
    }))
    setRows(newRows)
  }, [filteredLicenses])

  const handlePreviewClick = (id: GridRowId) => () => {
    navigate('/license/' + id)
  }

  const handleEditClick = (id: GridRowId) => () => {
    navigate('/license/' + id + '/edit')
  }

  const handleDeleteClick = (id: GridRowId) => () => {
    if (
      window.confirm(`Êtes-vous sûr de vouloir supprimer la licence ${id}?`)
    ) {
      axios
        .delete(`${base_url}/license/${id}`)
        .then(() => {
          toast.success('Licence supprimée avec succès.')
          setRows((prevRows) => prevRows.filter((row) => row.id !== id))
        })
        .catch((err) => {
          console.error('Failed to delete license:', err)
        })
    }
  }

  const columns: GridColDef[] = [
    {
      field: 'licenseKey',
      headerName: 'Clé de licence',
      flex: 1,
      minWidth: 280,
    },
    {
      field: 'isMedical',
      type: 'boolean',
      headerName: 'Médicale',
      width: 120,
    },
    {
      field: 'invitedUserLogin',
      headerName: 'Possesseur',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'usedDate',
      headerName: "Date d'utilisation",
      type: 'date',
      width: 180,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 125,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        return [
          <GridActionsCellItem
            icon={<PreviewIcon />}
            label="Aperçu"
            onClick={handlePreviewClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Modifier"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Supprimer"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ]
      },
    },
  ]

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          margin: '1rem',
          gap: '1rem',
        }}
      >
        <SearchBar
          onSearch={setFilteredLicenses}
          fields={searchFields}
          originalData={licenses}
        />
        <ExportButton
          fields={[
            'licenseKey',
            'isMedical',
            'usedDate',
            'invitedUserLogin',
            'creator',
          ]}
          data={licenses}
          exportFileName="licenses_export"
        />
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        slots={{ toolbar: GridToolbar }}
        autosizeOnMount={true}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 10 },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
        disableRowSelectionOnClick
      />
    </Box>
  )
}
